/**
 * Omnibox search hook. One debounced query feeds BOTH surfaces (the Cmd-K overlay and the /search
 * route) so they behave identically. The query only fires for a non-blank term of >= MIN_CHARS and
 * only when the user holds search:read — so a no-permission user never triggers a request that the
 * server would 403, and the UI shows a clear no-access state instead.
 *
 * The backend trims/normalizes the term itself (min_length=1); we add a small client-side floor +
 * debounce purely to avoid a request per keystroke. We never reconcile/merge the three result groups
 * — members, accounts, and invoices are distinct entities and stay separate end-to-end.
 */

import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useRbac } from "@/context/RbacContext";
import type { SearchResult } from "@/api/types";

/** Below this length we don't query (a single char floods with low-signal matches). */
export const MIN_SEARCH_CHARS = 2;
const DEBOUNCE_MS = 250;

export interface UseSearchResult {
  /** The raw input value (controlled by the caller's input). */
  term: string;
  /** The debounced term actually sent to the API. */
  debounced: string;
  /** True once the debounced term clears the min-length floor. */
  active: boolean;
  /** The user may run a search at all. */
  canSearch: boolean;
  data: SearchResult | undefined;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  /** True when a query is in flight for a term we don't yet have results for. */
  isLoading: boolean;
  refetch: () => void;
}

/** Debounce a value by `delay` ms. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function useSearch(term: string): UseSearchResult {
  const { hasPermission } = useRbac();
  const canSearch = hasPermission("search:read");

  const trimmed = term.trim();
  const debounced = useDebounced(trimmed, DEBOUNCE_MS).trim();
  const active = debounced.length >= MIN_SEARCH_CHARS;
  const enabled = canSearch && active;

  const query = useQuery({
    queryKey: ["search", debounced],
    queryFn: ({ signal }) => api.search(debounced, signal),
    enabled,
    // Keep the previous group results visible while the next term loads — no flicker to empty.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  return {
    term,
    debounced,
    active,
    canSearch,
    data: query.data,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    // "Loading" = enabled + fetching + nothing to show yet (first fetch for this term).
    isLoading: enabled && query.isFetching && query.data === undefined,
    refetch: () => void query.refetch(),
  };
}

/** Total number of hits across the groups (0 when no data). Pending invitees count: a term that
 *  matches ONLY a pending invite must not read "No results". */
export function searchHitCount(data: SearchResult | undefined): number {
  if (!data) return 0;
  return (
    data.members.length + (data.pending ?? []).length + data.accounts.length + data.invoices.length
  );
}
